// Up to three questions the club asks parents when they register for one
// class (a tournament's jersey name, a shirt size). Questions live on the
// class as {id, label, type: "text" | "select", options, required}; answers
// go up with each checkout item as {question_id: answer}.

export const MAX_CLASS_QUESTIONS = 3;

const clean = (value) => (value == null ? "" : String(value).trim());

/** Label of the first required question left blank, or null. */
export const firstUnanswered = (questions, answers) => {
  const given = answers || {};
  const missing = (questions || []).find(
    (q) => q.required && !clean(given[q.id]),
  );
  return missing ? missing.label : null;
};

/**
 * The answers to send for one child. A class with questions always sends an
 * object, even an empty one: an absent key is how the mobile app says "I did
 * not ask", and the server lets that through unchecked.
 */
export const answersPayload = (questions, answers) => {
  if (!questions || questions.length === 0) return undefined;
  const given = answers || {};
  const payload = {};
  questions.forEach((q) => {
    const value = clean(given[q.id]);
    if (value) payload[q.id] = value;
  });
  return payload;
};

/** Server questions -> class form rows (choices edited as one text box). */
export const questionsToForm = (questions) =>
  (questions || []).map((q) => ({
    id: q.id,
    label: q.label,
    type: q.type || "text",
    options_text: (q.options || []).join(", "),
    required: Boolean(q.required),
  }));

/** Class form rows -> what the server stores. Rows with no label are dropped. */
export const questionsFromForm = (rows) =>
  (rows || [])
    .filter((row) => clean(row.label))
    .map((row) => ({
      ...(row.id ? { id: row.id } : {}),
      label: clean(row.label),
      type: row.type,
      options:
        row.type === "select"
          ? (row.options_text || "")
              .split(",")
              .map((o) => o.trim())
              .filter(Boolean)
          : null,
      required: Boolean(row.required),
    }));

/** One roster/CSV cell per class question, in the class's order. */
export const rosterAnswerCells = (questions, student) => {
  const byId = {};
  (student?.custom_answers || []).forEach((a) => {
    byId[a.question_id] = a.answer;
  });
  return (questions || []).map((q) => byId[q.id] || "Not answered");
};
